import React, { useState, useEffect } from "react";
import {
  Loader,
  Table,
  Dropdown,
  Button,
  Icon,
  Message,
  Popup,
  Modal,
  Input,
  Checkbox,
} from "semantic-ui-react";
import { Energyform, Form } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { isAdmin, isMaster, isEditor, isReviewer } from "../../../../utils/checkPermission";
import "./ListEnergyForms.scss";
import { formatDateView } from "../../../../utils/formatDate";
import { EnergyForm } from "../EnergyForm";
import { PERIODS, normalizeUnit } from "../../../../utils";
import { BasicDrawer, ScrollToTop, ConfirmModal } from "../../../Shared";
import {
  energyCodes,
  determineEnergyUnits,
} from "../../../../utils/dataAndFormulas/energyForms";
import { useLanguage } from "../../../../contexts";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { UnitsForm } from "../UnitsForm";
import { UpdateUnitsModal } from "../UpdateUnitsModal";
import { computeCrossMonthRestrictions } from "../../../../utils/energyConflictGroups";
const _ = require("lodash");

const energyFormController = new Energyform();
const formController = new Form();

// Multi-medidor: clave normalizada para agrupar por nombre de medidor sin que
// mayúsculas/espacios generen medidores "distintos" por error de tipeo.
const normalizeMeterKey = (name) => (name || "").trim().toLowerCase();

// Multi-medidor: pares consumo/costo que deberían tener los mismos medidores
// cargados para un mismo mes (para el aviso de inconsistencia).
const METER_FIELD_PAIRS = {
  electricity_standard_combined: "electricity_cost_total",
  electricity_cost_total: "electricity_standard_combined",
  renewable_energies_ppa_combined: "renewable_energies_ppa_cost",
  renewable_energies_ppa_cost: "renewable_energies_ppa_combined",
  natural_gas_combined: "natural_gas_total_cost",
  natural_gas_total_cost: "natural_gas_combined",
  glp_combined: "glp_total_cost",
  glp_total_cost: "glp_combined",
  heavy_fuel_oil_total: "heavy_fuel_oil_total_cost",
  heavy_fuel_oil_total_cost: "heavy_fuel_oil_total",
  light_fuel_oil_total: "light_fuel_oil_total_cost",
  light_fuel_oil_total_cost: "light_fuel_oil_total",
  coal_total: "coal_total_cost",
  coal_total_cost: "coal_total",
  diesel_combined: "diesel_total_cost",
  diesel_total_cost: "diesel_combined",
  biodiesel_combined: "biodiesel_total_cost",
  biodiesel_total_cost: "biodiesel_combined",
  gasoline_combined: "gasoline_total_cost",
  gasoline_total_cost: "gasoline_combined",
  biofuel_combined: "biofuel_total_cost",
  biofuel_total_cost: "biofuel_combined",
  biomass_total: "biomass_total_cost",
  biomass_total_cost: "biomass_total",
  steam_total: "steam_total_cost",
  steam_total_cost: "steam_total",
  // renewable_energies_on_site_combined no tiene campo de costo pareado —
  // queda sin entrada acá, el aviso de "medidores no coinciden" no aplica.
};

// Multi-medidor: campos que realmente soportan records[] en el modelo de
// Mongo (ver MULTI_METER_FIELDS en server/controllers/energy.js — debe
// mantenerse igual a esa lista). Para cualquier otro campo (ej. las
// variantes "Doméstico"/"Producción" sueltas, sin "Combinada"), el schema
// no tiene declarado `records`, así que Mongo lo descarta en silencio: se
// puede "agregar un medidor", el total sube, pero el registro desaparece
// al volver a entrar. Por eso el modal de medidores solo se ofrece acá.
const MULTI_METER_FIELDS = [
  "electricity_standard_combined", "electricity_standard_domestic", "electricity_standard_production", "electricity_cost_total",
  "renewable_energies_ppa_combined", "renewable_energies_ppa_domestic", "renewable_energies_ppa_production", "renewable_energies_ppa_cost",
  "renewable_energies_on_site_combined", "renewable_energies_on_site_domestic", "renewable_energies_on_site_production",
  "natural_gas_combined", "natural_gas_cogeneration", "natural_gas_production_operations", "natural_gas_domestic", "natural_gas_total_cost",
  "glp_combined", "glp_cogeneration", "glp_production_operations", "glp_domestic", "glp_total_cost",
  "heavy_fuel_oil_total", "heavy_fuel_oil_total_cost",
  "light_fuel_oil_total", "light_fuel_oil_total_cost",
  "coal_total", "coal_total_cost",
  "diesel_combined", "diesel_cogeneration", "diesel_production_operations", "diesel_forklifts", "diesel_total_cost",
  "biodiesel_combined", "biodiesel_cogeneration", "biodiesel_production_operations", "biodiesel_forklifts", "biodiesel_total_cost",
  "gasoline_combined", "gasoline_cogeneration", "gasoline_production_operations", "gasoline_forklifts", "gasoline_total_cost",
  "biofuel_combined", "biofuel_cogeneration", "biofuel_production_operations", "biofuel_forklifts", "biofuel_total_cost",
  "biomass_total", "biomass_total_cost",
  "steam_total", "steam_total_cost",
];

export function ListEnergyForms(props) {
  const { reload, onReload, siteSelected, yearSelected, configFormData } =
    props;

  const [energyforms, setEnergyForms] = useState([]);
  const [loading, setLoading] = useState(false);

  const { user, accessToken } = useAuth();
  const { role, site, company } = user;

  const { language, translations } = useLanguage();

  const t = (key) => translations[key] || key;

  useEffect(() => {
    (async () => {
      if (yearSelected !== "" && siteSelected !== null) {
        setLoading(true);
        try {
          const response =
            await energyFormController.getEnergyFormsBySiteAndYear(
              accessToken,
              siteSelected,
              yearSelected
            );
          if (response.code === 200) {
            setEnergyForms(response.energyForms);
          } else {
            setEnergyForms([]);
          }
        } catch (error) {
          console.error(error);
          setEnergyForms([]);
        } finally {
          setLoading(false);
        }
      }
    })();
  }, [yearSelected, siteSelected, reload]);

  if (loading) return <Loader active inline="centered" content="Cargando datos de energía..." />;

  // Actualiza un formulario ya cargado en memoria sin disparar onReload().
  // onReload() cambia la prop `reload`, lo que reactiva el useEffect de arriba
  // y pone loading=true — eso desmonta <TablePeriods> (reemplazado por el
  // <Loader>) y con él se pierde cualquier estado local, como el modal de
  // registros de medidor abierto. Para poder guardar una edición sin cerrar
  // ese modal, actualizamos `energyforms` directamente en memoria.
  const patchEnergyForm = (itemId, field, fieldPayload) => {
    setEnergyForms((prev) =>
      prev.map((f) => (f._id === itemId ? { ...f, [field]: fieldPayload } : f))
    );
  };

  return (
    <div className="list-energy-forms">
      <TablePeriods
        data={energyforms}
        reload={reload}
        onReload={onReload}
        onPatchItem={patchEnergyForm}
        accessToken={accessToken}
        year={yearSelected}
        site={siteSelected}
        t={t}
        configFormData={configFormData}
        language={language}
        user={user}
      />
    </div>
  );
}

function TablePeriods(props) {
  const {
    data,
    reload,
    onReload,
    onPatchItem,
    accessToken,
    year,
    site,
    t,
    configFormData,
    language,
    user,
  } = props;

  const [showModal, setShowModal] = useState(false);
  const [titleModal, setTitleModal] = useState("");
  const [modalContent, setModalContent] = useState("");
  const [confirmContent, setConfirmContent] = useState("");
  const [dataDeleted, setDataDelete] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [uniqueFields, setUniqueFields] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [unitsRecorded, setUnitsRecorded] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const [selectedPeriod, setSelectedPeriod] = useState(null);

  // Estados para edición múltiple
  const [editMode, setEditMode] = useState(false);
  const [editedValues, setEditedValues] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Estado para expandir filas
  const [expandedFields, setExpandedFields] = useState({});

  // Multi-medidor: toggle "ver por medidor" (por campo) y modal de registros
  const [viewByMeterFields, setViewByMeterFields] = useState({});
  const [meterPopup, setMeterPopup] = useState(null); // { field, period, item } | null

  // Estado para el modal de configuración de unidades
  const [showUnitsModal, setShowUnitsModal] = useState(false);
  const [showUnitsWarning, setShowUnitsWarning] = useState(false);
  const [showMissingUnitsWarning, setShowMissingUnitsWarning] = useState(false);

  // Estado para el modal de modificar unidades en formularios existentes
  const [showUpdateUnitsModal, setShowUpdateUnitsModal] = useState(false);
  const [isUpdatingUnits, setIsUpdatingUnits] = useState(false);

  // Estado para el modal de advertencia de unidades
  const [showWarningModal, setShowWarningModal] = useState(false);

  // Estado para el modal de advertencia de cambio de unidades (l → L)
  const [showLegacyUnitWarning, setShowLegacyUnitWarning] = useState(false);
  const [pendingUnitsToSave, setPendingUnitsToSave] = useState(null);

  const onOpenCloseModal = () => setShowModal((prevState) => !prevState);
  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);
  const onOpenCloseUnitsModal = () => setShowUnitsModal((prevState) => !prevState);
  const onOpenCloseUpdateUnitsModal = () => setShowUpdateUnitsModal((prevState) => !prevState);

  const toggleExpand = (fieldName) => {
    setExpandedFields((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
  };

  const validateUnitsConfigured = () => {
    if (!configFormData?.fields || configFormData.fields.length === 0) {
      return { valid: true };
    }

    const activeFields = configFormData.fields.filter(field => field.active !== false);

    if (activeFields.length === 0) {
      return { valid: false, message: t("no_active_fields") };
    }

    const fieldsWithUnit = activeFields.filter(field => field.unit && field.unit !== "");

    if (fieldsWithUnit.length === 0) {
      return {
        valid: false,
        message: t("units_not_configured") || "Debe configurar al menos una unidad antes de agregar un periodo. Haga clic en el ícono de tres puntos (⋯) en la columna de Unidades."
      };
    }

    return { valid: true };
  };

  useEffect(() => {
    (async () => {
      try {
        if (site && year) {
          const fields = determineFields();
          setUniqueFields(fields);
        }
      } catch (error) {
        setUniqueFields([]);
      }
    })();
  }, [configFormData, site, year, reload]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges && editMode) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChanges, editMode]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (editMode) {
        if (e.ctrlKey && e.key === "s") {
          e.preventDefault();
          handleSaveChanges();
        } else if (e.key === "Escape") {
          handleCancelEdit();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [editMode, editedValues]);

  useEffect(() => {
    if (!uniqueFields?.length) return;

    const fetchUnits = async () => {
      try {
        let recordedUnits = {};

        if (configFormData?.fields && configFormData.fields.length > 0) {
          configFormData.fields.forEach((field) => {
            if (field.unit && field.name) {
              recordedUnits[field.name] = field.unit;
            }
          });
        }

        setUnitsRecorded(recordedUnits);
        setShowUnitsWarning(!configFormData && Object.keys(recordedUnits).length > 0);

        const values = {};
        uniqueFields.forEach((field) => {
          values[field] = recordedUnits[field] || "";
        });

        setSelectedUnits(values);

        if (configFormData?.fields && configFormData.fields.length > 0) {
          const activeFieldsWithoutUnit = configFormData.fields.filter(
            (field) => field.active && !field.unit
          );

          setShowMissingUnitsWarning(activeFieldsWithoutUnit.length > 0);
        } else {
          setShowMissingUnitsWarning(false);
        }
      } catch (error) {
        console.error(error);
        setUnitsRecorded({});
        const values = {};
        uniqueFields.forEach((field) => {
          values[field] = "";
        });
        setSelectedUnits(values);
      }
    };

    fetchUnits();
  }, [uniqueFields, configFormData, reload]);

  const handleDeleteModal = (period) => {
    const form = data.find((item) => item.period === period);
    setConfirmContent(
      `${t("delete_dated_energy_form")} ${formatDateView(form.date)}`
    );
    setDataDelete(form);
    onOpenCloseConfirm();
  };

  const openUpdateEnergyForm = (period) => {
    const form = data.find((item) => item.period === period);
    setTitleModal(
      `${t("update")} ${t("energy_form")}: ${t(form.period)}-${form.year}`
    );
    const crossMonthRestrictions = computeCrossMonthRestrictions(data, period);
    setModalContent(
      <EnergyForm
        onClose={() => setShowModal(false)}
        onReload={onReload}
        siteSelected={site}
        energyForm={form}
        configFormData={configFormData}
        unitsRecorded={unitsRecorded}
        crossMonthRestrictions={crossMonthRestrictions}
      />
    );
    setShowModal(true);
  };

  const openNewEnergyForm = (period) => {
    const validation = validateUnitsConfigured();
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    const crossMonthRestrictions = computeCrossMonthRestrictions(data, null);
    setTitleModal(t("new_energy_form"));
    setModalContent(
      <EnergyForm
        onClose={() => setShowModal(false)}
        onReload={onReload}
        period={period}
        year={year}
        siteSelected={site}
        configFormData={configFormData}
        unitsRecorded={unitsRecorded}
        crossMonthRestrictions={crossMonthRestrictions}
      />
    );
    setShowModal(true);
  };

  const handleUnitsFormSave = (units) => {
    const hasExistingUnits = configFormData?.fields?.some(f => f.unit);
    if (hasExistingUnits) {
      setPendingUnitsToSave(units);
      setShowLegacyUnitWarning(true);
    } else {
      handleSaveUnits(units);
    }
  };

  const handleSaveUnits = async (units) => {
    try {
      if (!configFormData?._id || !configFormData?.fields || configFormData.fields.length === 0) {
        const allEnergyFieldsData = [
          { name: "electricity_standard_combined", code: "1.1" },
          { name: "electricity_standard_domestic", code: "1.2" },
          { name: "electricity_standard_production", code: "1.3" },
          { name: "electricity_cost_total", code: "1.4" },
          { name: "renewable_energies_ppa_combined", code: "2.1" },
          { name: "renewable_energies_ppa_domestic", code: "2.2" },
          { name: "renewable_energies_ppa_production", code: "2.3" },
          { name: "renewable_energies_ppa_cost", code: "2.4" },
          { name: "renewable_energies_on_site_combined", code: "3.1" },
          { name: "renewable_energies_on_site_domestic", code: "3.2" },
          { name: "renewable_energies_on_site_production", code: "3.3" },
          { name: "renewable_energies_on_site_maintenance_and_amortization", code: "3.4" },
          { name: "natural_gas_combined", code: "4.1" },
          { name: "natural_gas_cogeneration", code: "4.2" },
          { name: "natural_gas_production_operations", code: "4.3" },
          { name: "natural_gas_domestic", code: "4.4" },
          { name: "natural_gas_total_cost", code: "4.5" },
          { name: "glp_combined", code: "5.1" },
          { name: "glp_cogeneration", code: "5.2" },
          { name: "glp_production_operations", code: "5.3" },
          { name: "glp_domestic", code: "5.4" },
          { name: "glp_total_cost", code: "5.5" },
          { name: "heavy_fuel_oil_total", code: "6.1" },
          { name: "heavy_fuel_oil_total_cost", code: "6.2" },
          { name: "light_fuel_oil_total", code: "7.1" },
          { name: "light_fuel_oil_total_cost", code: "7.2" },
          { name: "coal_total", code: "8.1" },
          { name: "coal_total_cost", code: "8.2" },
          { name: "diesel_combined", code: "9.1" },
          { name: "diesel_cogeneration", code: "9.2" },
          { name: "diesel_production_operations", code: "9.3" },
          { name: "diesel_forklifts", code: "9.4" },
          { name: "diesel_total_cost", code: "9.5" },
          { name: "biodiesel_combined", code: "10.1" },
          { name: "biodiesel_cogeneration", code: "10.2" },
          { name: "biodiesel_production_operations", code: "10.3" },
          { name: "biodiesel_forklifts", code: "10.4" },
          { name: "biodiesel_total_cost", code: "10.5" },
          { name: "gasoline_combined", code: "11.1" },
          { name: "gasoline_cogeneration", code: "11.2" },
          { name: "gasoline_production_operations", code: "11.3" },
          { name: "gasoline_forklifts", code: "11.4" },
          { name: "gasoline_total_cost", code: "11.5" },
          { name: "biofuel_combined", code: "12.1" },
          { name: "biofuel_cogeneration", code: "12.2" },
          { name: "biofuel_production_operations", code: "12.3" },
          { name: "biofuel_forklifts", code: "12.4" },
          { name: "biofuel_total_cost", code: "12.5" },
          { name: "biomass_total", code: "13.1" },
          { name: "biomass_total_cost", code: "13.2" },
          { name: "steam_total", code: "14.1" },
          { name: "steam_total_cost", code: "14.2" },
          { name: "biodiesel_renewable_percentage", code: "15.1" },
          { name: "gasoline_renewable_percentage", code: "15.2" },
          { name: "irec_certificates", code: "15.3" },
        ];

        const fieldsToCreate = allEnergyFieldsData.map((field) => ({
          code: field.code,
          name: field.name,
          active: true,
          unit: units[field.name] || "",
          standardized_coefficient: 1,
        }));

        const newFormData = {
          module: "energy",
          site: site,
          fields: fieldsToCreate,
        };

        const response = await formController.createForm(accessToken, newFormData);

        if (response.code === 200 || response.code === 201) {
          toast.success(t("units_updated_successfully"));
          setShowUnitsWarning(false);
          onOpenCloseUnitsModal();
          onReload();
        } else {
          toast.error(t("error_updating_units"));
        }
        return;
      }

      const formId = configFormData._id;
      const currentFields = configFormData.fields || [];

      // Campos que deben existir siempre (agregados recientemente)
      const requiredFields = [
        { name: "biodiesel_renewable_percentage", code: "15.1" },
        { name: "gasoline_renewable_percentage", code: "15.2" },
        { name: "irec_certificates", code: "15.3" },
      ];
      const existingNames = currentFields.map((f) => f.name);
      const missingFields = requiredFields
        .filter((f) => !existingNames.includes(f.name))
        .map((f) => ({ code: f.code, name: f.name, active: true, unit: units[f.name] || "", standardized_coefficient: 1 }));

      // Solo se envían los campos que cambian de unidad (o son nuevos), de
      // forma atómica — no se pisa el documento entero (ver updateFormFields
      // en el controlador), para no perder un toggle de 'active' guardado
      // desde Fuentes casi al mismo tiempo.
      const changes = [
        ...currentFields
          .filter((field) => Object.prototype.hasOwnProperty.call(units, field.name))
          .map((field) => ({ name: field.name, unit: units[field.name] })),
        ...missingFields,
      ];

      const response =
        changes.length > 0
          ? await formController.updateFormFields(accessToken, formId, changes)
          : { code: 200 };

      if (response.code === 200 || response.code === 201) {
        toast.success(t("units_updated_successfully"));
        setShowUnitsWarning(false);
        onOpenCloseUnitsModal();
        onReload();
      } else {
        toast.error(t("error_updating_units"));
      }
    } catch (error) {
      console.error(error);
      toast.error(t("error_updating_units"));
    }
  };

  const handleApplyUnitsToForms = async (unitsToApply) => {
    if (!unitsToApply || Object.keys(unitsToApply).length === 0) {
      toast.warning(t("no_fields_selected") || "No hay campos seleccionados");
      return;
    }

    setIsUpdatingUnits(true);
    try {
      const response = await energyFormController.updateUnitsForYear(
        accessToken,
        site,
        year,
        unitsToApply
      );

      if (response.code === 200) {
        toast.success(
          `${t("units_applied_to_forms") || "Unidades aplicadas a"} ${response.updated} ${t("forms") || "formularios"}`
        );
        onOpenCloseUpdateUnitsModal();
        onReload();
      } else {
        toast.error(t("error_applying_units_to_forms") || "Error al aplicar unidades");
      }
    } catch (error) {
      console.error("Error applying units:", error);
      toast.error(t("error_applying_units_to_forms") || "Error al aplicar unidades");
    } finally {
      setIsUpdatingUnits(false);
    }
  };

  const onDelete = async () => {
    try {
      const response = await energyFormController.deleteEnergyForm(
        accessToken,
        dataDeleted._id
      );
      if (response.code === 200) {
        setIsSuccess(true);
        setModalMessage("Formulario eliminado correctamente!");
      } else {
        setIsSuccess(false);
        setModalMessage("Error al eliminar el formulario.");
      }

      setModalOpen(true);
      setDataDelete("");
      onOpenCloseConfirm();
      onReload();
    } catch (error) {
      console.error(error);
      setIsSuccess(false);
      setModalMessage("Error al eliminar el formulario.");
      setModalOpen(true);
    }
  };

  const periods = PERIODS;

  const determineFields = () => {
    const fields = new Set();
    if (
      configFormData &&
      configFormData.fields &&
      configFormData.fields.length > 0
    ) {
      Object.keys(energyCodes).forEach((key) => {
        if (
          configFormData.fields &&
          configFormData.fields.some(
            (field) => field.name === key && field.active
          )
        ) {
          fields.add(key);
        }
      });
    } else {
      Object.keys(energyCodes).forEach((key) => {
        fields.add(key);
      });
    }
    return Array.from(fields);
  };

  const hasDataPeriod = (period) => {
    return data.some((entry) => entry.period === period);
  };

  // Multi-medidor: cantidad de medidores distintos que aportan a un campo en el año
  // (agrupados por nombre normalizado — "Medidor A" y "medidor a " cuentan como uno solo)
  const getDistinctMeterCount = (field) => {
    const keys = new Set();
    data.forEach((item) => {
      (item[field]?.records || []).forEach((r) => keys.add(normalizeMeterKey(r.meterName)));
    });
    return keys.size;
  };

  // Multi-medidor: desglose {meterName: {period: value}} para las sub-filas.
  // Se agrupa por nombre normalizado; se muestra el nombre tal como se escribió
  // la primera vez que se vio ese medidor.
  const getMeterBreakdown = (field) => {
    const byMeter = {};
    data.forEach((item) => {
      (item[field]?.records || []).forEach((r) => {
        const key = normalizeMeterKey(r.meterName);
        if (!byMeter[key]) byMeter[key] = { displayName: (r.meterName || "").trim(), monthly: {} };
        byMeter[key].monthly[item.period] = (byMeter[key].monthly[item.period] || 0) + (r.value || 0);
      });
    });
    return Object.values(byMeter).map(({ displayName, monthly }) => [displayName, monthly]);
  };

  // Multi-medidor: nombres de medidor ya usados en el año, sin importar el campo
  // (para ofrecerlos en el selector del modal de agregar registro). Deduplicados
  // por nombre normalizado, se ofrece el nombre tal como se escribió primero.
  const getAllMeterNames = () => {
    const seen = new Map();
    data.forEach((item) => {
      uniqueFields.forEach((field) => {
        (item[field]?.records || []).forEach((r) => {
          const key = normalizeMeterKey(r.meterName);
          if (key && !seen.has(key)) seen.set(key, (r.meterName || "").trim());
        });
      });
    });
    return Array.from(seen.values()).sort();
  };

  // Multi-medidor: ¿los medidores cargados en este campo para este mes no
  // coinciden con los del campo pareado (consumo <-> costo)? Solo avisa, no bloquea.
  const hasMeterMismatch = (field, period) => {
    const pairField = METER_FIELD_PAIRS[field];
    if (!pairField) return false;
    const item = data.find((d) => d.period === period);
    const namesA = new Set((item?.[field]?.records || []).map((r) => normalizeMeterKey(r.meterName)));
    const namesB = new Set((item?.[pairField]?.records || []).map((r) => normalizeMeterKey(r.meterName)));
    if (namesA.size === 0 && namesB.size === 0) return false;
    if (namesA.size !== namesB.size) return true;
    for (const key of namesA) if (!namesB.has(key)) return true;
    return false;
  };

  const calculateTotalAndAverage = (field) => {
    const values = data.map((item) => {
      const val = item[field]?.value;
      return val ?? 0;
    });

    const totalFinal = values.reduce((ac, value) => ac + value, 0);
    const total = formatNumberWithThousands(totalFinal);
    const averageFinal = values.length ? totalFinal / values.length : 0;
    const average = formatNumberWithThousands(averageFinal);
    return { total, average };
  };

  const getUnitName = (field) => {
    const unitValue = normalizeUnit(selectedUnits[field]);

    if (!unitValue || unitValue === "") {
      return "- - -";
    }

    const unitsArray = determineEnergyUnits(field);
    const unitObject = unitsArray.find(u => u.value === unitValue);
    return unitObject?.name || unitValue;
  };

  function formatNumberWithThousands(numero) {
    if (typeof numero !== "number" || isNaN(numero)) {
      return numero;
    }
    const entero = Math.round(numero);
    return entero.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }

  const handleCellChange = (field, period, item, newValue) => {
    const key = `${period}_${field}`;
    setEditedValues((prev) => ({
      ...prev,
      [key]: {
        formId: item?._id || null,
        period: period,
        field,
        value: newValue,
        unit: item?.[field]?.unit || selectedUnits[field],
        isNew: !item || !item._id,
        year: year,
        site: site,
      },
    }));
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!hasChanges || Object.keys(editedValues).length === 0) {
      toast.info("No hay cambios para guardar");
      return;
    }

    setIsSaving(true);

    try {
      const updates = {};
      const creates = [];

      Object.entries(editedValues).forEach(([key, change]) => {
        if (change.isNew) {
          creates.push({
            period: change.period,
            year: change.year,
            site: change.site,
            field: change.field,
            value: change.value,
            unit: change.unit,
          });
        } else {
          updates[key] = {
            formId: change.formId,
            field: change.field,
            value: change.value,
            unit: change.unit,
          };
        }
      });

      const response = await energyFormController.batchUpdateEnergyForms(
        accessToken,
        {
          updates,
          creates,
          site,
          year,
          creatorUser: user._id,
        }
      );

      if (response.code === 200) {
        toast.success(`✅ ${response.updated || 0} formularios actualizados, ${response.created || 0} creados`);
        setEditedValues({});
        setHasChanges(false);
        setEditMode(false);
        onReload();
      } else {
        toast.error(response.msg || "Error al guardar los cambios");
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error("Error al guardar los cambios");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (hasChanges) {
      const confirmed = window.confirm(
        "Hay cambios sin guardar. ¿Deseas cancelar la edición?"
      );
      if (!confirmed) return;
    }
    setEditMode(false);
    setEditedValues({});
    setHasChanges(false);
  };

  const toggleEditMode = () => {
    if (editMode && hasChanges) {
      const confirmed = window.confirm(
        "Hay cambios sin guardar. ¿Deseas salir del modo edición?"
      );
      if (!confirmed) return;
    }
    setEditMode(!editMode);
    setEditedValues({});
    setHasChanges(false);
  };

  return (
    <>
      {/* Botones de control de edición múltiple */}
      {(isMaster(user.role) || isAdmin(user.role) || isEditor(user.role)) && (
      <div className="batch-actions">
        {!editMode ? (

           <Popup
              content={t("enable_multiple_edit")}
              trigger={
                     <Button
            className="batch-btn primary"
            icon
            // labelPosition="left"
            onClick={toggleEditMode}
            disabled={data.length === 0}
          >
            <Icon name="edit" />
            {/* {t("enable_multiple_edit") || "Habilitar edición múltiple"} */}
          </Button> }
            />
        ) : (
          <>
            <Button
              className="batch-btn success"
              icon
              labelPosition="left"
              onClick={handleSaveChanges}
              disabled={!hasChanges || isSaving}
              loading={isSaving}
            >
              <Icon name="save" />
              {t("save_changes") || "Guardar cambios"}
            </Button>
            <Button
              className="batch-btn cancel"
              icon
              labelPosition="left"
              onClick={handleCancelEdit}
              disabled={isSaving}
            >
              <Icon name="cancel" />
              {t("cancel") || "Cancelar"}
            </Button>
            {hasChanges && (
              <span style={{ color: "#f57c00", fontWeight: "bold" }}>
                <Icon name="warning sign" />
                {Object.keys(editedValues).length} cambio(s) pendiente(s)
              </span>
            )}
          </>
        )}
      </div>
      )}

      {hasChanges && (
        <Message warning>
          <Message.Header>Cambios sin guardar</Message.Header>
          <p>
            Tienes {Object.keys(editedValues).length} cambio(s) pendiente(s). No
            olvides guardar antes de salir. (Ctrl+S para guardar, Esc para cancelar)
          </p>
        </Message>
      )}

      {showUnitsWarning && (
        <Message info>
          <Message.Header>Configuración de unidades recomendada</Message.Header>
          <p>
            Las unidades mostradas provienen de formularios guardados. Para una mejor consistencia,
            se recomienda configurar las unidades explícitamente haciendo clic en el ícono de tres puntos (⋯)
            en la columna "Unidades".
          </p>
        </Message>
      )}

      <div className="list-energy-forms__table-wrapper">
        <Table className="energy-table" unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className="sticky-col-1 pill-left-start">
                {t("code")}
              </Table.HeaderCell>
              <Table.HeaderCell className="sticky-col-2">
                {t("concept")}
              </Table.HeaderCell>
              <Table.HeaderCell className="pill-left-end">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                  {t("units")}
                  {showMissingUnitsWarning && (
                    <Icon
                      name="warning sign"
                      color="orange"
                      style={{ cursor: "pointer", margin: 0 }}
                      onClick={() => setShowWarningModal(true)}
                    />
                  )}
                  <Dropdown
                    icon="ellipsis vertical"
                    floating
                    className="icon"
                    style={{ cursor: "pointer" }}
                  >
                    <Dropdown.Menu>
                      <Dropdown.Item
                        text={t("configure_units") || "Configurar unidades"}
                        icon="setting"
                        onClick={onOpenCloseUnitsModal}
                      />
                      {data.length > 0 && (
                        <Dropdown.Item
                          text={t("update_units_in_forms") || "Modificar unidades en formularios"}
                          icon="edit"
                          onClick={onOpenCloseUpdateUnitsModal}
                        />
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </Table.HeaderCell>
              <Table.HeaderCell className="pill-gap" />
              <Table.HeaderCell colSpan="3" textAlign="center" className="pill-right">
                {t("report_period")} {year}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {uniqueFields.map((field) => {
              const isExpanded = expandedFields[field];
              const { total, average } = calculateTotalAndAverage(field);
              const hasMismatchedUnit = data.some((item) => {
                const itemUnit = normalizeUnit(item?.[field]?.unit);
                const configuredUnit = normalizeUnit(selectedUnits[field]);
                return itemUnit && configuredUnit && itemUnit !== configuredUnit;
              });
              const distinctMeters = getDistinctMeterCount(field);
              const showByMeter = viewByMeterFields[field] !== false;
              const isMeterCapableField = MULTI_METER_FIELDS.includes(field);

              return (
                <React.Fragment key={field}>
                  {/* Fila resumen */}
                  <Table.Row>
                    <Table.Cell className="sticky-col-1 pill-left-start">
                      {energyCodes[field]}
                    </Table.Cell>
                    <Table.Cell className="sticky-col-2">
                      {t(field)}
                      {distinctMeters > 1 && (
                        <span
                          style={{
                            marginLeft: "0.5em",
                            background: "#e8f4fd",
                            color: "#1a6ba0",
                            borderRadius: "12px",
                            padding: "1px 8px",
                            fontSize: "0.75em",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          ⚡ {distinctMeters} {t("meters") || "medidores"}
                        </span>
                      )}
                    </Table.Cell>
                    <Table.Cell textAlign="center" className="pill-left-end">
                      {getUnitName(field)}
                      {hasMismatchedUnit && (
                        <Popup
                          content="Hay meses con unidades diferentes a la configurada"
                          trigger={
                            <Icon
                              name="warning sign"
                              color="orange"
                              size="small"
                              style={{ cursor: "pointer", marginLeft: "4px" }}
                            />
                          }
                        />
                      )}
                    </Table.Cell>
                    <Table.Cell className="pill-gap" />
                    <Table.Cell colSpan="3" className="pill-right">
                      <div className="summary-flex">
                        <span className="summary-item">
                          {t("total")}: <strong>{total}</strong>
                        </span>
                        <span className="summary-item">
                          {t("average")}: <strong>{average}</strong>
                        </span>
                        <Button
                          icon
                          size="tiny"
                          className="expand-btn"
                          onClick={() => toggleExpand(field)}
                        >
                          <Icon name={isExpanded ? "chevron up" : "chevron down"} />
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>

                  {/* Fila cabecera de meses (expandida) */}
                  {isExpanded && (
                    <Table.Row className="months-header-row">
                      <Table.Cell colSpan="7" style={{ padding: 0 }} className="months-full-row">
                        <div className="months-flex-container">
                          {periods.map((period) => (
                            <div key={period} className="month-flex-cell month-flex-header">
                              {t(period).slice(0, 3)}
                              {!editMode && (
                                hasDataPeriod(period) ? (
                                  <Dropdown
                                    inline
                                    size="tiny"
                                    icon="ellipsis vertical"
                                    floating
                                    className="icon"
                                  >
                                    <Dropdown.Menu>
                                      <Dropdown.Item
                                        text={t("edit")}
                                        icon={<Icon name="edit" style={{ color: "gray" }} />}
                                        onClick={() => openUpdateEnergyForm(period)}
                                      />
                                      <Dropdown.Item
                                        text={t("delete")}
                                        icon={<Icon name="trash" style={{ color: "red" }} />}
                                        onClick={() => handleDeleteModal(period)}
                                      />
                                    </Dropdown.Menu>
                                  </Dropdown>
                                ) : (
                                  <Dropdown
                                    link
                                    size="tiny"
                                    icon="ellipsis vertical"
                                    floating
                                    className="icon"
                                  >
                                    <Dropdown.Menu>
                                      <Dropdown.Item
                                        text={t("load_data")}
                                        icon="plus"
                                        onClick={() => openNewEnergyForm(period)}
                                      />
                                    </Dropdown.Menu>
                                  </Dropdown>
                                )
                              )}
                            </div>
                          ))}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  )}

                  {/* Fila datos de meses (expandida) */}
                  {isExpanded && (
                    <Table.Row className="months-data-row">
                      <Table.Cell colSpan="7" style={{ padding: 0 }} className="months-full-row">
                        <div className="months-flex-container">
                          {periods.map((period) => {
                            const item = data.find((d) => d.period === period);
                            const editKey = `${period}_${field}`;
                            const isEdited = editedValues[editKey] !== undefined;
                            const hasUnit = selectedUnits[field] && selectedUnits[field] !== "";
                            const itemUnit = normalizeUnit(item?.[field]?.unit);
                            const configuredUnit = normalizeUnit(selectedUnits[field]);
                            const isDifferentUnit = itemUnit && configuredUnit && itemUnit !== configuredUnit;
                            let cellContent;

                            if (editMode) {
                              const displayVal = isEdited
                                ? editedValues[editKey].value
                                : (item?.[field]?.value ?? "");
                              cellContent = (
                                <input
                                  type="number"
                                  step="0.01"
                                  value={displayVal}
                                  onChange={(e) =>
                                    handleCellChange(
                                      field,
                                      period,
                                      item,
                                      parseFloat(e.target.value) || 0
                                    )
                                  }
                                  disabled={!hasUnit || isReviewer(user.role)}
                                  style={{
                                    width: "100%",
                                    height:"30px",
                                    border: isEdited ? "2px solid #fbc02d" : "1px solid #ccc",
                                    borderRadius: "4px",
                                    padding: "2px 6px",
                                    textAlign: "right",
                                    fontSize: "11px",
                                    background: hasUnit ? "white" : "#f5f5f5",
                                    color: !hasUnit ? "#999" : isReviewer(user.role) ? "#000" : "inherit",
                                    MozAppearance: "textfield",
                                    appearance: "textfield",
                                  }}
                                  placeholder="-"
                                />
                              );
                            } else {
                              const recordCount = item?.[field]?.records?.length || 0;
                              cellContent = item?.[field] ? (
                                <span>
                                  {formatNumberWithThousands(item[field].value)}
                                  {isDifferentUnit && (
                                    <span
                                      style={{ fontSize: "0.75em", color: "#f57c00", marginLeft: "2px" }}
                                      title={`Unidad: ${itemUnit} | Configurada: ${configuredUnit}`}
                                    >
                                      ({itemUnit})
                                    </span>
                                  )}
                                  {recordCount > 1 && (
                                    <span
                                      style={{
                                        marginLeft: "0.3em", background: "#1a2a4a", color: "#fff",
                                        borderRadius: "50%", padding: "0 5px", fontSize: "0.7em",
                                      }}
                                    >
                                      {recordCount}
                                    </span>
                                  )}
                                  {hasMeterMismatch(field, period) && (
                                    <Popup
                                      content={t("meter_mismatch_warning") || "Los medidores cargados acá no coinciden con los del campo de consumo/costo pareado para este mes."}
                                      trigger={
                                        <Icon
                                          name="warning sign"
                                          color="orange"
                                          size="small"
                                          style={{ marginLeft: "0.3em", cursor: "pointer" }}
                                        />
                                      }
                                    />
                                  )}
                                </span>
                              ) : "-";
                            }

                            return (
                              <div
                                key={period}
                                className="month-flex-cell month-flex-data"
                                style={!editMode && isMeterCapableField ? { cursor: "pointer" } : undefined}
                                title={!editMode && isMeterCapableField ? (t("view_add_meter_records") || "Ver / agregar registros del mes") : undefined}
                                onClick={
                                  !editMode && isMeterCapableField
                                    ? () => setMeterPopup({ field, period, item: item || null })
                                    : undefined
                                }
                              >
                                {cellContent}
                              </div>
                            );
                          })}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  )}

                  {/* Multi-medidor: toggle + sub-filas por medidor (expandida) */}
                  {isExpanded && distinctMeters > 1 && (
                    <>
                      <Table.Row className="months-header-row">
                        <Table.Cell className="sticky-col-1" style={{ padding: "4px 8px" }}>
                          <Checkbox
                            toggle
                            label={t("view_by_meter") || "Ver por medidor"}
                            checked={showByMeter}
                            onChange={(_, d) =>
                              setViewByMeterFields((prev) => ({ ...prev, [field]: d.checked }))
                            }
                          />
                        </Table.Cell>
                        <Table.Cell colSpan="6" />
                      </Table.Row>
                      {showByMeter &&
                        getMeterBreakdown(field).map(([meterName, monthly]) => (
                          <Table.Row key={meterName} className="months-data-row">
                            <Table.Cell colSpan="7" style={{ padding: 0 }} className="months-full-row">
                              <div style={{ padding: "4px 12px", fontSize: "0.95em", color: "#648EBC", fontWeight: 600 }}>
                                <Icon name="microchip" /> {meterName}
                              </div>
                              <div className="months-flex-container">
                                {periods.map((period) => (
                                  <div key={period} className="month-flex-cell month-flex-data" style={{ color: "#888" }}>
                                    {monthly[period] != null ? formatNumberWithThousands(monthly[period]) : "-"}
                                  </div>
                                ))}
                              </div>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </Table.Body>
        </Table>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={1200}
        hideProgressBar
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <ScrollToTop />

      <BasicDrawer
        show={showModal}
        close={() => setShowModal(false)}
        title={titleModal}
      >
        {modalContent}
      </BasicDrawer>

      <ConfirmModal
        open={showConfirm}
        title={t("delete")}
        content={confirmContent}
        onConfirm={onDelete}
        onCancel={onOpenCloseConfirm}
        danger
      />

      <ConfirmModal
        open={modalOpen}
        icon={<Icon name={isSuccess ? "check circle outline" : "warning sign"} color={isSuccess ? "green" : "red"} />}
        title={isSuccess ? "Éxito" : "Error"}
        content={modalMessage}
        onCancel={() => setModalOpen(false)}
        cancelText="Cerrar"
      />

      <UnitsForm
        show={showUnitsModal}
        onClose={onOpenCloseUnitsModal}
        onSave={handleUnitsFormSave}
        currentUnits={unitsRecorded}
        registeredFields={configFormData?.fields || []}
      />

      <UpdateUnitsModal
        show={showUpdateUnitsModal}
        onClose={onOpenCloseUpdateUnitsModal}
        onApply={handleApplyUnitsToForms}
        currentUnits={unitsRecorded}
        registeredFields={configFormData?.fields || []}
        isLoading={isUpdatingUnits}
      />

      <ConfirmModal
        open={showWarningModal}
        icon={<Icon name="warning sign" color="orange" />}
        title="Unidades sin configurar"
        content={
          <>
            <p>Los campos no tienen unidades asignadas en la configuración.</p>
            <p>Por favor configure las unidades haciendo clic en el ícono <strong>⋯</strong> en la columna Unidades.</p>
          </>
        }
        onCancel={() => setShowWarningModal(false)}
        onConfirm={() => { setShowWarningModal(false); onOpenCloseUnitsModal(); }}
        cancelText="Cerrar"
        confirmText="Configurar unidades"
      />

      <ConfirmModal
        open={showLegacyUnitWarning}
        icon={<Icon name="warning sign" color="orange" />}
        title="Cambio de unidades"
        content={
          <>
            <p>
              Los registros anteriores continuarán guardados con la unidad{" "}
              <strong>"l"</strong> (litros). A partir de ahora, los nuevos
              datos se guardarán con <strong>"L"</strong> (Litros).
            </p>
            <p>¿Desea continuar con el cambio?</p>
          </>
        }
        onCancel={() => setShowLegacyUnitWarning(false)}
        onConfirm={() => {
          setShowLegacyUnitWarning(false);
          handleSaveUnits(pendingUnitsToSave);
        }}
        cancelText="Cancelar"
        confirmText="Confirmar"
      />

      {meterPopup && (
        <MeterRecordsModal
          meterPopup={meterPopup}
          onClose={() => setMeterPopup(null)}
          accessToken={accessToken}
          site={site}
          year={year}
          user={user}
          t={t}
          existingMeterNames={getAllMeterNames()}
          canEdit={isAdmin(user.role) || isEditor(user.role) || isMaster(user.role)}
          onSaved={() => {
            setMeterPopup(null);
            onReload();
          }}
          onSavedKeepOpen={(fieldPayload) => {
            setMeterPopup((prev) =>
              prev ? { ...prev, item: { ...(prev.item || {}), [prev.field]: fieldPayload } } : prev
            );
            // A propósito NO llamamos a onReload() acá: eso pondría loading=true
            // en el componente padre y desmontaría este modal. En cambio,
            // actualizamos el registro en memoria para que la tabla de fondo
            // (totales, badge de medidores) quede sincronizada sin cerrar el modal.
            if (meterPopup?.item?._id) {
              onPatchItem(meterPopup.item._id, meterPopup.field, fieldPayload);
            }
          }}
        />
      )}
    </>
  );
}

// Multi-medidor: modal para ver los registros de un mes/campo y agregar uno nuevo a mano.
function MeterRecordsModal({ meterPopup, onClose, accessToken, site, year, user, t, existingMeterNames = [], canEdit = true, onSaved, onSavedKeepOpen }) {
  const { field, period, item } = meterPopup;
  const [meterName, setMeterName] = useState("");
  const [meterOptions, setMeterOptions] = useState(existingMeterNames);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  // null = agregando un registro nuevo; número = editando existingRecords[editingIndex]
  const [editingIndex, setEditingIndex] = useState(null);
  // Se muestra cuando hay un valor cargado sin `records[]` (campo nunca
  // pasado por este multi-medidor, ej. cargado a mano antes de que existiera
  // esta función) y el usuario intenta agregar un registro nuevo — antes se
  // pisaba ese valor en silencio (ver newRecords = [...existingRecords, ...]
  // con existingRecords vacío). Ahora se frena y se le pregunta si quiere
  // sumar el valor viejo como un primer registro, o descartarlo.
  const [showLegacyPrompt, setShowLegacyPrompt] = useState(false);

  const existingRecords = item?.[field]?.records || [];
  const currentTotal = item?.[field]?.value;
  const currentUnit = item?.[field]?.unit;
  const hasLegacyValue = existingRecords.length === 0 && !!currentTotal;

  // Medidor cargado en el campo pareado (consumo <-> costo) que todavía no
  // está cargado acá — mismo criterio que hasMeterMismatch, pero mostrando
  // el/los nombres puntuales para que se puedan completar sin salir del modal.
  const pairField = METER_FIELD_PAIRS[field];
  const pairRecords = item?.[pairField]?.records || [];
  const ownKeys = new Set(existingRecords.map((r) => normalizeMeterKey(r.meterName)));
  const missingHere = pairRecords.filter((r) => !ownKeys.has(normalizeMeterKey(r.meterName)));

  const resetForm = () => {
    setMeterName("");
    setValue("");
    setEditingIndex(null);
    setShowLegacyPrompt(false);
  };

  const handleStartEdit = (i) => {
    if (!canEdit) return;
    setMeterName(existingRecords[i].meterName);
    setValue(String(existingRecords[i].value));
    setEditingIndex(i);
  };

  const handleDelete = async (i) => {
    if (!canEdit || !item?._id) return;
    setSaving(true);
    try {
      const newRecords = existingRecords.filter((_, idx) => idx !== i);
      const newTotal = newRecords.reduce((s, r) => s + (r.value || 0), 0);
      await energyFormController.updateEnergyForm(accessToken, item._id, { [field]: { records: newRecords, value: newTotal } });
      toast.success(t("meter_record_deleted") || "Registro eliminado");
      if (editingIndex === i) resetForm();
      onSaved();
    } catch (error) {
      console.error(error);
      toast.error(t("error_saving_meter_record") || "Error al guardar el registro");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (resolution = null) => {
    if (!canEdit) return;
    const numValue = parseFloat(value);
    if (!meterName.trim() || isNaN(numValue)) {
      toast.warning(t("meter_name_and_value_required") || "Completá nombre de medidor y valor");
      return;
    }
    const isEditing = editingIndex != null;
    // Frenamos ACÁ, antes de tocar el server, para preguntar qué hacer con
    // el valor viejo — el usuario elige "sumar"/"reemplazar" y eso vuelve a
    // llamar a handleSave(resolution) directamente.
    if (!isEditing && hasLegacyValue && !resolution) {
      setShowLegacyPrompt(true);
      return;
    }
    setSaving(true);
    try {
      const newRecord = { meterName: meterName.trim(), value: numValue, source: "manual", addedBy: user?._id };
      const newRecords = isEditing
        ? existingRecords.map((r, idx) =>
            idx === editingIndex ? { ...r, meterName: meterName.trim(), value: numValue } : r
          )
        : resolution === "sum"
        ? [
            {
              meterName: t("previous_value_meter_label") || "Valor anterior",
              value: currentTotal,
              source: item?.[field]?.sourceHash ? "invoice_reader" : "manual",
            },
            newRecord,
          ]
        : [...existingRecords, newRecord];
      const newTotal = newRecords.reduce((s, r) => s + (r.value || 0), 0);
      const fieldPayload = { records: newRecords, value: newTotal };

      if (item?._id) {
        await energyFormController.updateEnergyForm(accessToken, item._id, { [field]: fieldPayload });
      } else {
        const createResponse = await energyFormController.createEnergyForm(accessToken, {
          site,
          year,
          period,
          creator_user: user._id,
          date: new Date(),
          [field]: fieldPayload,
        });
        // Condición de carrera: otro request creó el formulario de este
        // período justo antes que nosotros. En vez de perder nuestro
        // registro, lo agregamos al documento que ganó la carrera.
        if (createResponse?.alreadyExisted && createResponse?.energyForm?._id) {
          const winnerRecords = createResponse.energyForm?.[field]?.records || [];
          const mergedRecords = [...winnerRecords, ...newRecords.slice(existingRecords.length)];
          const mergedTotal = mergedRecords.reduce((s, r) => s + (r.value || 0), 0);
          await energyFormController.updateEnergyForm(accessToken, createResponse.energyForm._id, {
            [field]: { records: mergedRecords, value: mergedTotal },
          });
        }
      }
      toast.success(isEditing ? (t("meter_record_updated") || "Registro actualizado") : (t("meter_record_added") || "Registro agregado"));
      resetForm();
      // Al editar un registro existente dejamos el modal abierto para poder
      // seguir revisando/editando otros registros del mismo mes.
      if (isEditing) {
        onSavedKeepOpen(fieldPayload);
      } else {
        onSaved();
      }
    } catch (error) {
      console.error(error);
      toast.error(t("error_saving_meter_record") || "Error al guardar el registro");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} size="tiny" className="meter-records-modal">
      <Modal.Header>
        <div className="meter-records-modal__title">
          {(t("meter_records_for") || "Registros de")} {t(period)} — {t(field)}
        </div>
      </Modal.Header>
      <Modal.Content>
        {existingRecords.length > 0 ? (
          <Table basic="very" compact size="small">
            <Table.Body>
              {existingRecords.map((r, i) => (
                <Table.Row key={i} active={editingIndex === i}>
                  {editingIndex === i ? (
                    <>
                      <Table.Cell>
                        <Input
                          size="mini"
                          fluid
                          value={meterName}
                          onChange={(e) => setMeterName(e.target.value)}
                          disabled={saving}
                          autoFocus
                        />
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <Input
                          size="mini"
                          type="number"
                          value={value}
                          onChange={(e) => setValue(e.target.value)}
                          disabled={saving}
                          style={{ width: 90 }}
                        />
                      </Table.Cell>
                      {canEdit && (
                        <Table.Cell textAlign="right" collapsing>
                          <Button
                            icon
                            circular
                            color="green"
                            size="mini"
                            className="meter-record-btn"
                            style={{ width: 24, height: 24, minWidth: 24, minHeight: 24, maxWidth: 24, maxHeight: 24, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
                            onClick={() => handleSave()}
                            loading={saving}
                            disabled={saving}
                            title={t("save_changes") || "Guardar cambios"}
                          >
                            <Icon name="check" style={{ margin: 0 }} />
                          </Button>
                          <Button
                            icon
                            circular
                            size="mini"
                            className="meter-record-btn"
                            style={{ width: 24, height: 24, minWidth: 24, minHeight: 24, maxWidth: 24, maxHeight: 24, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1, backgroundColor: "#B7B7B7" }}
                            onClick={resetForm}
                            disabled={saving}
                            title={t("cancel_edit") || "Cancelar edición"}
                          >
                            <Icon name="close" style={{ margin: 0, color: "#000" }} />
                          </Button>
                        </Table.Cell>
                      )}
                    </>
                  ) : (
                    <>
                      <Table.Cell>{r.meterName}</Table.Cell>
                      <Table.Cell textAlign="right">{r.value} {currentUnit}</Table.Cell>
                      {canEdit && (
                        <Table.Cell textAlign="right" collapsing>
                          <Button
                            icon
                            circular
                            primary
                            size="mini"
                            className="meter-record-btn"
                            style={{ width: 24, height: 24, minWidth: 24, minHeight: 24, maxWidth: 24, maxHeight: 24, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
                            onClick={() => handleStartEdit(i)}
                            disabled={saving || editingIndex != null}
                            title={t("edit") || "Editar"}
                          >
                            <Icon name="pencil" style={{ margin: 0 }} />
                          </Button>
                          <Button
                            icon
                            circular
                            color="red"
                            size="mini"
                            className="meter-record-btn"
                            style={{ width: 24, height: 24, minWidth: 24, minHeight: 24, maxWidth: 24, maxHeight: 24, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
                            onClick={() => handleDelete(i)}
                            disabled={saving || editingIndex != null}
                            title={t("delete") || "Eliminar"}
                          >
                            <Icon name="trash" style={{ margin: 0 }} />
                          </Button>
                        </Table.Cell>
                      )}
                    </>
                  )}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        ) : (
          <p style={{ color: "#888" }}>
            {currentTotal != null
              ? `${t("current_value") || "Valor actual"}: ${currentTotal} ${currentUnit || ""}`
              : (t("no_records_yet") || "Todavía no hay registros para este mes.")}
          </p>
        )}
        {missingHere.length > 0 && (
          <Message warning size="small" style={{ marginTop: "1em" }}>
            <Icon name="warning sign" />
            {(t("meter_missing_here_prefix") || "Ya está cargado en")} {t(pairField)}, {(t("meter_missing_here_suffix") || "pero no acá")}:{" "}
            <strong>{missingHere.map((r) => r.meterName).join(", ")}</strong>
          </Message>
        )}
        {showLegacyPrompt && (
          <Message warning size="small" style={{ marginTop: "1em" }}>
            <Icon name="warning sign" />
            {t("legacy_value_conflict_warning") || "Ya hay un valor cargado sin medidor asociado"}
            {" "}({currentTotal} {currentUnit}). {t("legacy_value_conflict_question") || "¿Qué querés hacer con ese valor?"}
            <div style={{ display: "flex", gap: "8px", marginTop: "0.5em" }}>
              <Button size="small" primary onClick={() => handleSave("sum")} loading={saving} disabled={saving}>
                {t("sum_to_existing_value") || "Sumar al valor existente"}
              </Button>
              <Button size="small" onClick={() => handleSave("replace")} loading={saving} disabled={saving}>
                {t("replace_existing_value") || "Reemplazar valor existente"}
              </Button>
              <Button size="small" basic onClick={() => setShowLegacyPrompt(false)} disabled={saving}>
                {t("cancel") || "Cancelar"}
              </Button>
            </div>
          </Message>
        )}
        {canEdit && (
          // Se mantiene siempre montado (con visibility, no display:none)
          // aunque se esté editando una fila arriba, para que el modal no
          // cambie de ancho/alto al entrar y salir de edición.
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginTop: "1em",
              alignItems: "center",
              visibility: editingIndex == null ? "visible" : "hidden",
            }}
          >
            <Dropdown
              size="small"
              placeholder={t("meter_name") || "Nombre del medidor"}
              search
              selection
              allowAdditions
              additionLabel={`${t("add_new_meter") || "Nuevo medidor"}: `}
              options={meterOptions.map((name) => ({ key: name, text: name, value: name }))}
              value={editingIndex == null ? meterName : ""}
              onAddItem={(_, d) => setMeterOptions((prev) => [...prev, d.value].sort())}
              onChange={(_, d) => setMeterName(d.value)}
              style={{ flex: 2 }}
            />
            <Input
              size="small"
              type="number"
              placeholder={t("value") || "Valor"}
              value={editingIndex == null ? value : ""}
              onChange={(e) => setValue(e.target.value)}
              style={{ flex: 1 }}
            />
          </div>
        )}
        {!canEdit && (
          <Message size="small" style={{ marginTop: "1em" }}>
            {t("no_permission_add_meter") || "Tu rol no tiene permiso para agregar registros de medidor."}
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        {canEdit && (
          <Button
            className="meter-modal-btn meter-modal-btn--primary"
            style={{ minWidth: 110, visibility: editingIndex == null ? "visible" : "hidden" }}
            onClick={() => handleSave()}
            loading={saving}
            disabled={saving || editingIndex != null}
          >
            <Icon name="plus" />
            {t("add") || "Agregar"}
          </Button>
        )}
        <Button className="meter-modal-btn meter-modal-btn--cancel" style={{ minWidth: 110 }} onClick={onClose} disabled={saving}>
          {t("close") || "Cerrar"}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
